import { createFileRoute, Link } from "@tanstack/react-router";
import { Icon } from "@/components/Icon";
import { FaqCards } from "@/components/FaqAccordion";
import { Button } from "@/components/ui/button";
import { featuredLoanProducts, type LoanProduct } from "@/lib/loan-products";
import { serviceAreas } from "@/lib/service-areas";
import { AGENCY } from "@/lib/agency";
import { buildHead } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import heroBg from "@/assets/hero-bg.webp";

/* ---------- Unsplash imagery (descriptive alt text required at use site) ---------- */
const img = (id: string, w = 800) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=60`;

const HERO_ID = "photo-1556761175-5973dc0f32e7";
const HERO_SRC = img(HERO_ID, 1200);

const IMAGES = {
  hero: HERO_SRC,
  heroSecondary: img("photo-1556742400-b5b7c5121f9b", 500),
  storefront: img("photo-1441986300917-64674bd600d8", 600),
  handshake: img("photo-1521791136064-7986c2920216", 700),
  advisor: img("photo-1573496359142-b8d87734a5a2", 600),
  callCenter: img("photo-1573164713988-8665fc963095", 700),
  closing: img("photo-1554224155-6726b3ff858f", 1100),
  step1: img("photo-1573497019940-1c28c88b4f3e", 480),
  step2: img("photo-1551434678-e076c223a692", 480),
  step3: img("photo-1454165804606-c3d57bc86b40", 480),
  step4: img("photo-1554224154-22dec7ec8818", 480),
  prod: {
    "working-capital": img("photo-1556742502-ec7c0e9f34b1", 500),
    "line-of-credit": img("photo-1554224155-1696413565d3", 500),
    "same-day-loans": img("photo-1556742400-b5b7c5121f9b", 500),
    "invoice-factoring": img("photo-1450101499163-c8848c66ca85", 500),
    "equipment-financing": img("photo-1581094794329-c8112a89af12", 500),
    "sba-loans": img("photo-1554224154-26032cdc0a82", 500),
    "industry-specific": img("photo-1556761175-b413da4baf72", 500),
  } as Record<string, string>,
  industries: {
    "Restaurants & hospitality": img("photo-1517248135467-4c7edcad34c4", 480),
    "Medical & dental": img("photo-1576091160550-2173dba999ef", 480),
    Construction: img("photo-1504307651254-35680f356dfd", 480),
    "Retail & e-commerce": img("photo-1472851294608-062f824d29cc", 480),
    "Trucking & logistics": img("photo-1601584115197-04ecc0da31d7", 480),
    "Auto & repair": img("photo-1486262715619-67b85e0b08d3", 480),
    "Professional services": img("photo-1551836022-d5d88e9218df", 480),
    Manufacturing: img("photo-1565793298595-6a879b1d9492", 480),
  } as Record<string, string>,
};

export const Route = createFileRoute("/")({
  head: () => {
    const head = buildHead({
      title: `${AGENCY.name} - Business Loans in Dallas, TX`,
      description:
        "Fast, honest business financing for Dallas-Fort Worth small and mid-sized companies. $5K to $5M. No upfront fees. Decisions in hours.",
      path: "/",
      image: IMAGES.hero,
      breadcrumbs: [{ name: "Home", path: "/" }],
    });
    return {
      ...head,
      links: [
        ...head.links,
        { rel: "preload", as: "image", href: heroBg, fetchPriority: "high" },
      ],
    };
  },
  component: Home,
});

const eyebrow = "text-xs font-semibold uppercase tracking-[0.18em]";

function Home() {
  return (
    <>
      <Hero />
      <Reassurance />
      <Financing />
      <WhyUs />
      <Products />
      <KeyFacts />
      <HowItWorks />
      <Industries />
      <Qualify />
      <FAQ />
      <FinalCTA />
    </>
  );
}

/* ---------------- Section shell ---------------- */

function Band({
  tone = "light",
  id,
  children,
  className = "",
}: {
  tone?: "light" | "soft" | "dark";
  id?: string;
  children: React.ReactNode;
  className?: string;
}) {
  const toneClass =
    tone === "dark"
      ? "bg-primary text-primary-foreground"
      : tone === "soft"
      ? "bg-secondary text-foreground"
      : "bg-background text-foreground";
  return (
    <section
      id={id}
      className={`relative overflow-hidden border-b border-border ${toneClass} ${className}`}
    >
      <div className="relative mx-auto w-full max-w-7xl px-6 py-20 md:py-28 lg:px-10">
        {children}
      </div>
    </section>
  );
}

function SectionHead({
  num,
  eyebrow: e,
  title,
  intro,
  dark,
  align = "center",
}: {
  num: string;
  eyebrow: string;
  title: React.ReactNode;
  intro?: string;
  dark?: boolean;
  align?: "center" | "left";
}) {
  const muted = dark ? "text-primary-foreground/65" : "text-muted-foreground";
  const alignCls = align === "left" ? "text-left" : "mx-auto text-center";
  const eyebrowJustify = align === "left" ? "justify-start" : "justify-center";
  return (
    <div className={`${alignCls} max-w-3xl`}>
      <p
        className={`${eyebrow} text-accent flex items-center gap-2 ${eyebrowJustify}`}
      >
        <span className="h-px w-8 bg-current opacity-60" />
        {e}
      </p>
      <h2 className="mt-5 font-serif text-4xl font-semibold leading-[1.05] tracking-tight md:text-5xl">
        {title}
      </h2>
      {intro ? (
        <p
          className={`mt-5 max-w-2xl text-base leading-relaxed md:text-lg ${muted} ${
            align === "center" ? "mx-auto" : ""
          }`}
        >
          {intro}
        </p>
      ) : null}
    </div>
  );
}

function GhostNumber(_: { children: React.ReactNode }) {
  return null;
}

/* ---------------- Hero ---------------- */

function Hero() {
  return (
    <section className="relative overflow-hidden bg-primary text-primary-foreground">
      {/* background image */}
      <img
        src={heroBg}
        alt="Dallas downtown skyline at dusk"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-40"
        width={1600}
        height={1067}
        decoding="async"
        fetchPriority="high"
        loading="eager"
      />
      {/* dark gradient overlay for readability */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-r from-primary via-primary/85 to-primary/40"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 85% 0%, oklch(0.42 0.16 250) 0%, transparent 55%), radial-gradient(ellipse at 0% 100%, oklch(0.3 0.1 260) 0%, transparent 50%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-6 py-24 md:py-32 lg:px-10">
        <Reveal className="max-w-3xl">
          <p
            className={`${eyebrow} inline-flex items-center gap-2 rounded-full border border-primary-foreground/20 bg-primary-foreground/5 px-4 py-1.5 text-primary-foreground/85`}
          >
            <Icon name="mappin" color="bl" eager className="h-3.5 w-3.5 brightness-0 invert"  />
            Based in {AGENCY.city}
          </p>
          <h1 className="mt-7 font-serif text-5xl font-semibold leading-[1.02] tracking-tight md:text-6xl xl:text-7xl">
            Business Loans & Funding
            <span className="text-accent"> in Dallas, TX.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-primary-foreground/85 md:text-xl">
            {AGENCY.name} arranges working capital, lines of credit, and
            specialty loans for {AGENCY.area}. One advisor. Real answers.
            Funding in days, sometimes hours.
          </p>
          <ul className="mt-7 grid max-w-2xl gap-2 text-sm text-primary-foreground/90 sm:grid-cols-2">
            {[
              { icon: "dollarsign", t: "$5K to $5M available" },
              { icon: "clock", t: "Decisions in 1 hour" },
              { icon: "handcoins", t: "No upfront fees, ever" },
              { icon: "shieldcheck", t: "No obligation to accept" },
            ].map(({ icon: __iconName, t }) => (
              <li key={t} className="flex items-center gap-2">
                <Icon name={__iconName} color="co" eager className="h-4 w-4 shrink-0 text-accent" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Button
              asChild
              size="lg"
              className="h-12 bg-accent px-7 text-base text-accent-foreground transition hover:-translate-y-0.5 hover:bg-accent/90 hover:shadow-lg"
            >
              <Link to="/apply-funding">Apply Now</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="h-12 border-primary-foreground/30 bg-transparent px-7 text-base text-primary-foreground transition hover:bg-primary-foreground/10 hover:text-primary-foreground"
            >
              <a href={AGENCY.phoneHref}>
                <Icon name="phonecall" color="bl" className="h-4 w-4 brightness-0 invert"  />
                Call {AGENCY.phone}
              </a>
            </Button>
          </div>

          {/* trust row */}
          <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-xs text-primary-foreground/75">
            <span className="inline-flex items-center gap-1.5">
              <Icon name="star" color="co" className="h-3.5 w-3.5"  />
              4.9 average advisor rating
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Icon name="shieldcheck" color="co" className="h-3.5 w-3.5"  />
              Soft credit pull to start
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Icon name="building2" color="co" className="h-3.5 w-3.5"  />
              Texas-based team
            </span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- Reassurance ---------------- */

function Reassurance() {
  const items = [
    {
      title: "No upfront fees",
      sub: "You only pay if you fund. Never a dollar to apply or shop offers.",
      svg: (
        <svg viewBox="0 0 64 64" className="h-8 w-8" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="wos-g1" x1="0" x2="1" y1="0" y2="1">
              <stop offset="0%" stopColor="#34d399" />
              <stop offset="100%" stopColor="#0ea5e9" />
            </linearGradient>
          </defs>
          <circle cx="32" cy="32" r="26" fill="url(#wos-g1)" opacity="0.18" />
          <circle cx="32" cy="32" r="20" fill="url(#wos-g1)" />
          <text x="32" y="40" textAnchor="middle" fontSize="22" fontWeight="700" fill="#fff" fontFamily="ui-sans-serif, system-ui">$0</text>
        </svg>
      ),
    },
    {
      title: "No obligation to proceed",
      sub: "Review your options, then decide. Walking away is always free.",
      svg: (
        <svg viewBox="0 0 64 64" className="h-8 w-8" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="wos-g2" x1="0" x2="1" y1="0" y2="1">
              <stop offset="0%" stopColor="#6366f1" />
              <stop offset="100%" stopColor="#8b5cf6" />
            </linearGradient>
          </defs>
          <path d="M32 6 L54 14 V32 C54 44 44 54 32 58 C20 54 10 44 10 32 V14 Z" fill="url(#wos-g2)" />
          <path d="M22 32 l8 8 l14 -16" stroke="#fff" strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
    },
    {
      title: "One dedicated advisor",
      sub: "Same person from first call to funded. No handoffs or call queues.",
      svg: (
        <svg viewBox="0 0 64 64" className="h-8 w-8" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="wos-g3" x1="0" x2="1" y1="0" y2="1">
              <stop offset="0%" stopColor="#f97316" />
              <stop offset="100%" stopColor="#ef4444" />
            </linearGradient>
          </defs>
          <circle cx="32" cy="32" r="26" fill="url(#wos-g3)" opacity="0.18" />
          <circle cx="32" cy="24" r="9" fill="url(#wos-g3)" />
          <path d="M14 52 c2 -10 10 -16 18 -16 s16 6 18 16 z" fill="url(#wos-g3)" />
        </svg>
      ),
    },
    {
      title: "Fast decisions",
      sub: "Soft-pull pre-qualification in about an hour during business hours.",
      svg: (
        <svg viewBox="0 0 64 64" className="h-8 w-8" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="wos-g4" x1="0" x2="1" y1="0" y2="1">
              <stop offset="0%" stopColor="#facc15" />
              <stop offset="100%" stopColor="#f97316" />
            </linearGradient>
          </defs>
          <circle cx="32" cy="32" r="26" fill="url(#wos-g4)" opacity="0.2" />
          <path d="M34 6 L14 36 H30 L26 58 L50 28 H34 Z" fill="url(#wos-g4)" stroke="#fff" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
      ),
    },
  ];
  return (
    <section className="relative overflow-hidden border-b border-border bg-secondary">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          background:
            "radial-gradient(60% 80% at 50% 0%, color-mix(in oklab, var(--accent) 10%, transparent), transparent 70%)",
        }}
      />
      <div className="relative mx-auto max-w-7xl px-6 py-16 md:py-20 lg:px-10">
        <div className="mx-auto max-w-2xl text-center">
          <p className={`${eyebrow} flex items-center justify-center gap-2 text-accent`}>
            <span className="h-px w-8 bg-current opacity-60" />
            Why owners stay with us
          </p>
          <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight tracking-tight md:text-4xl">
            Honest by design, fast by default.
          </h2>
        </div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map(({ title, sub, svg }, i) => (
            <Reveal key={title} delay={i * 80}>
              <div className="group relative h-full overflow-hidden rounded-2xl border border-border bg-background p-6 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-accent/40 hover:shadow-xl">
                <div
                  aria-hidden
                  className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-accent/10 blur-2xl opacity-0 transition duration-500 group-hover:opacity-100"
                />
                <div className="relative flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-background to-secondary ring-1 ring-border transition group-hover:scale-110">
                  {svg}
                </div>
                <h3 className="relative mt-5 font-serif text-lg font-semibold leading-snug">
                  {title}
                </h3>
                <p className="relative mt-2 text-sm leading-relaxed text-muted-foreground">
                  {sub}
                </p>
                <span
                  aria-hidden
                  className="relative mt-5 block h-px w-10 bg-accent/40 transition-all duration-300 group-hover:w-20"
                />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}


/* ---------------- Financing (image-left) ---------------- */

function Financing() {
  const tiers = [
    {
      icon: "wallet",
      title: "Short-term capital",
      copy: "Working capital, same-day loans, and lines of credit for cash flow, payroll, and quick opportunities.",
      range: "$5K - $250K",
    },
    {
      icon: "trendingup",
      title: "Growth financing",
      copy: "Equipment financing, expansion loans, and SBA programs for businesses ready to scale.",
      range: "$50K - $5M",
    },
    {
      icon: "filetext",
      title: "Receivables & specialty",
      copy: "Invoice factoring and industry-specific programs shaped to how your sector gets paid.",
      range: "Up to 90% A/R",
    },
  ];
  return (
    <Band tone="light">
      <GhostNumber>02</GhostNumber>
      <div className="grid items-start gap-16 lg:grid-cols-[1fr_1.05fr]">
        <Reveal className="relative">
          <div
            aria-hidden
            className="absolute -left-6 -top-6 h-40 w-40 rounded-2xl bg-accent/10"
          />
          <div className="relative overflow-hidden rounded-2xl border border-border shadow-xl">
            <img
              src={IMAGES.storefront}
              alt="Independent Dallas storefront on a sunlit street"
              className="aspect-[4/5] w-full object-cover"
              loading="lazy"
              width={600} height={750}
              decoding="async"
            />
          </div>
          <div className="absolute -bottom-6 -right-6 hidden rounded-xl border border-border bg-background p-4 shadow-xl md:flex md:items-center md:gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10 text-accent">
              <Icon name="banknote" color="co" className="h-5 w-5"  />
            </span>
            <div className="leading-tight">
              <p className="font-serif text-xl font-semibold">$5K - $5M</p>
              <p className="text-[11px] text-muted-foreground">
                Funding range
              </p>
            </div>
          </div>
        </Reveal>

        <div>
          <SectionHead
            num="02"
            eyebrow="What you can get"
            title={<>Financing built around how your business actually runs.</>}
            intro="Short-term and long-term funding from $5,000 up to $5 million, covering everything from a few weeks of payroll to a multi-year expansion."
            align="left"
          />
          <div className="mt-10 grid gap-4">
            {tiers.map((c, i) => (
              <Reveal
                key={c.title}
                delay={i * 80}
                className="group flex gap-5 rounded-xl border border-border bg-card p-6 transition hover:-translate-y-0.5 hover:border-accent hover:shadow-lg"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent ring-1 ring-accent/20 transition group-hover:bg-accent group-hover:text-accent-foreground">
                  <Icon name={c.icon} color="co" className="h-5 w-5"  />
                </span>
                <div className="flex-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h3 className="font-serif text-xl font-semibold">
                      {c.title}
                    </h3>
                    <p className={`${eyebrow} text-accent`}>{c.range}</p>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {c.copy}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </Band>
  );
}

/* ---------------- Why Us (dark, image-right) ---------------- */

function WhyUs() {
  const items = [
    {
      icon: "badgecheck",
      title: "One advisor, start to finish.",
      copy: "You'll work with the same person from your first call through funding. No handoffs, no repeating your story.",
    },
    {
      icon: "search",
      title: "We shop the market for you.",
      copy: "Banks offer one product. Portals send your data everywhere. We compare real offers from a vetted lender network on your behalf.",
    },
    {
      icon: "filetext",
      title: "Plain-English terms.",
      copy: "Every offer comes with a clear breakdown of cost, payments, and trade-offs, so you can decide with eyes open.",
    },
    {
      icon: "briefcase",
      title: "Built for owners, not committees.",
      copy: "We know what underwriters want and how to present your business so good operators don't get penalized for thin paperwork.",
    },
  ];
  return (
    <Band tone="dark">
      <div
        aria-hidden
        className="pointer-events-none absolute -top-24 right-0 h-80 w-80 rounded-full bg-accent/20 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-0 left-1/4 h-64 w-64 rounded-full bg-primary-foreground/5 blur-3xl"
      />
      <div className="grid items-center gap-14 lg:grid-cols-[1.1fr_1fr]">
        <div>
          <SectionHead
            dark
            align="left"
            num="03"
            eyebrow="Why work with us"
            title={<>The opposite of a bank. The opposite of a portal.</>}
          />
          <div className="mt-10 grid gap-5 sm:grid-cols-2">
            {items.map((it, i) => (
              <Reveal
                key={it.title}
                delay={i * 80}
                className="rounded-xl border border-primary-foreground/15 bg-primary-foreground/[0.04] p-6 backdrop-blur-sm transition hover:-translate-y-0.5 hover:border-accent/50 hover:bg-primary-foreground/[0.07]"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/15 text-accent ring-1 ring-accent/30">
                    <Icon name={it.icon} color="co" className="h-5 w-5"  />
                  </span>
                  <span className="font-serif text-2xl font-semibold text-accent">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="mt-4 font-serif text-lg font-semibold">
                  {it.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-primary-foreground/75">
                  {it.copy}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal delay={120} className="relative">
          <div
            aria-hidden
            className="absolute -inset-3 rounded-3xl bg-accent/30 blur-2xl"
          />
          <div className="relative overflow-hidden rounded-3xl border border-primary-foreground/15 shadow-2xl">
            <img
              src={IMAGES.advisor}
              alt="A Compass Lenders financing advisor walking a client through loan offers"
              className="aspect-[4/5] w-full object-cover"
              loading="lazy"
              width={600} height={750}
              decoding="async"
            />
            <div
              aria-hidden
              className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent"
            />
          </div>
          <div className="absolute bottom-6 left-6 right-6 rounded-xl border border-primary-foreground/15 bg-primary/70 p-4 backdrop-blur">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
              Promise
            </p>
            <p className="mt-1 text-sm text-primary-foreground/90">
              You'll never be passed to a stranger mid-deal.
            </p>
          </div>
        </Reveal>
      </div>
    </Band>
  );
}

/* ---------------- Products ---------------- */

const PRODUCT_ICON_NAME: Record<string, string> = {
  "sba-loans": "award",
  "sba-7a-loan": "badgecheck",
  "sba-loan-for-daycare": "heart",
  "sba-loan-for-franchise": "store",
  "sba-express-loan-for-veterans": "shieldcheck",
  "working-capital-loans": "wallet",
  "business-line-of-credit": "creditcard",
  "small-business-loans": "briefcase",
  "short-term-business-loans": "zap",
  "merchant-cash-advance": "receipt",
  "unsecured-business-loans": "sparkles",
  "startup-business-loans": "lightbulb",
  "business-acquisition-loans": "handcoins",
  "accounts-receivable-financing": "filetext",
  "invoice-factoring": "receipt",
  "revenue-based-financing": "trendingup",
  "same-day-business-loans": "clock",
  "business-lenders-near-me": "mappin",
  "business-loan-broker": "compass",
  "commercial-loan-broker": "building2",
  "equipment-financing": "wrench",
  "manufacturing-equipment-financing": "factory",
  "agriculture-equipment-financing": "tractor",
  "landscaping-equipment-financing": "scissors",
  "dental-practice-loans": "stethoscope",
  "medical-practice-loans": "stethoscope",
  "restaurant-loans": "utensils",
  "trucking-business-loans": "truck",
  "commercial-construction-loan": "hammer",
  "hotel-loans": "hotel",
  "salon-business-loans": "scissors",
  "retail-business-loans": "store",
  "auto-repair-shop-financing": "car",
  "ecommerce-business-funding": "shoppingbag",
  "food-truck-financing": "coffee",
  "loan-for-gym-business": "dumbbell",
  "farm-credit-financing": "sprout",
  "women-owned-business-funding": "users",
};

function ProductCard({ p, i }: { p: LoanProduct; i: number }) {
  const iconName = PRODUCT_ICON_NAME[p.slug] ?? "dollarsign";
  return (
    <Reveal delay={i * 60}>
      <Link
        to="/dallas/$slug"
        params={{ slug: p.slug }}
        className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-background p-7 shadow-sm transition hover:-translate-y-1 hover:border-accent hover:shadow-xl"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-accent/10 blur-2xl transition group-hover:bg-accent/20"
        />
        <div className="relative flex items-center justify-between">
          <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-accent/15 ring-1 ring-accent/30">
            <Icon name={iconName} color="co" className="h-8 w-8" />
          </span>
          <span className="rounded-full border border-border bg-secondary px-3 py-1 text-[11px] font-semibold text-foreground">
            {p.range}
          </span>
        </div>
        <h3 className="relative mt-6 font-serif text-xl font-semibold leading-tight">
          {p.name}
        </h3>
        <p className="relative mt-2 text-sm leading-relaxed text-muted-foreground">
          {p.tagline}
        </p>
        <div className="relative mt-4 flex items-center gap-2 text-xs text-muted-foreground">
          <Icon name="clock" color="bl" className="h-3.5 w-3.5 text-accent"  />
          <span>{p.speed}</span>
        </div>
        <div className="relative mt-auto pt-6 inline-flex items-center gap-2 text-sm font-semibold text-accent">
          Learn more
          <Icon name="chevronright" color="bl" className="h-4 w-4 transition group-hover:translate-x-1"  />
        </div>
      </Link>
    </Reveal>
  );
}

function Products() {
  return (
    <Band tone="soft" id="loan-products">
      <GhostNumber>04</GhostNumber>
      <Reveal>
        <SectionHead
          num="04"
          eyebrow="Loan products"
          title={<>Pick the financing that fits the job.</>}
          intro="Tap any product to see typical terms, who it's best for, and how to apply."
        />
      </Reveal>
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {featuredLoanProducts.slice(0, 6).map((p, i) => (
          <ProductCard key={p.slug} p={p} i={i} />
        ))}
      </div>
      <Reveal>
        <div className="mt-12 flex justify-center">
          <Link
            to="/dallas"
            className="group inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/5 px-7 py-3.5 text-sm font-semibold text-accent shadow-sm transition hover:border-accent hover:bg-accent hover:text-accent-foreground hover:shadow-lg"
          >
            View all loan products
            <Icon name="chevronright" color="bl" className="h-4 w-4 transition group-hover:translate-x-1"  />
          </Link>
        </div>
      </Reveal>
    </Band>
  );
}

/* ---------------- Key facts ---------------- */

function KeyFacts() {
  const facts = [
    { icon: "banknote", k: "Funding range", v: "$5K - $5M" },
    { icon: "clock", k: "Typical funding speed", v: "24-72 hrs" },
    { icon: "handcoins", k: "Cost to apply", v: "$0" },
    { icon: "building2", k: "Operating since", v: "2014" },
  ];
  return (
    <section className="relative overflow-hidden border-b border-border bg-accent text-accent-foreground">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full bg-accent-foreground/10 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 right-0 h-96 w-96 rounded-full bg-primary/30 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "radial-gradient(currentColor 1px, transparent 1px)",
          backgroundSize: "22px 22px",
        }}
      />
      <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10 md:py-24">
        <Reveal className="mx-auto max-w-2xl text-center">
          <span className={`${eyebrow} text-accent-foreground/70`}>
            By the numbers
          </span>
          <h2 className="mt-3 font-serif text-3xl font-semibold tracking-tight md:text-4xl">
            Straightforward funding, on your timeline.
          </h2>
        </Reveal>

        <div className="relative mt-14 grid grid-cols-1 gap-px overflow-hidden rounded-2xl bg-accent-foreground/15 ring-1 ring-accent-foreground/20 sm:grid-cols-2 lg:grid-cols-4">
          {facts.map((f, i) => (
            <Reveal
              key={f.k}
              delay={i * 80}
              className="group relative bg-accent p-8 transition-colors duration-300 hover:bg-accent-foreground/[0.04]"
            >
              <span
                aria-hidden
                className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent-foreground/40 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
              />
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent-foreground/10 text-accent-foreground ring-1 ring-accent-foreground/25 transition-transform duration-300 group-hover:scale-110 group-hover:bg-accent-foreground/15">
                <Icon name={f.icon} color="co" className="h-5 w-5"  />
              </span>
              <div className="mt-6 font-serif text-4xl font-semibold leading-none tracking-tight md:text-5xl">
                {f.v}
              </div>
              <div className="mt-4 h-px w-10 bg-accent-foreground/30" />
              <span className={`${eyebrow} mt-4 block text-accent-foreground/75`}>
                {f.k}
              </span>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- How it works ---------------- */

function HowItWorks() {
  const steps = [
    {
      icon: "phonecall",
      title: "Call or apply online",
      copy: "Tell us about your business in a quick conversation or a 5-minute form. No credit pull to start.",
    },
    {
      icon: "search",
      title: "We review and shop",
      copy: "Your advisor packages your file and shops it across our lender network to surface real, comparable offers.",
    },
    {
      icon: "checkcircle2",
      title: "Review your offer",
      copy: "We walk you through the terms in plain English. You decide what makes sense, with no pressure to proceed.",
    },
    {
      icon: "wallet",
      title: "Funds in your account",
      copy: "Once you accept, funds land in your business account in as little as 24 hours.",
    },
  ];
  return (
    <Band tone="light">
      <GhostNumber>05</GhostNumber>
      <Reveal>
        <SectionHead
          num="05"
          eyebrow="How it works"
          title={<>Four steps from first call to funded.</>}
        />
      </Reveal>
      <ol className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {steps.map((s, i) => (
          <Reveal
            as="li"
            key={s.title}
            delay={i * 80}
            className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card p-7 shadow-sm transition hover:-translate-y-1 hover:border-accent hover:shadow-xl"
          >
            <div
              aria-hidden
              className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-accent/10 blur-2xl transition group-hover:bg-accent/20"
            />
            <div className="relative flex items-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent/15 text-accent ring-1 ring-accent/30">
                <Icon name={s.icon} color="co" className="h-6 w-6"  />
              </span>
            </div>
            <h3 className="relative mt-6 font-serif text-lg font-semibold">
              {s.title}
            </h3>
            <p className="relative mt-2 text-sm leading-relaxed text-muted-foreground">
              {s.copy}
            </p>
          </Reveal>
        ))}
      </ol>
    </Band>
  );
}

/* ---------------- Industries ---------------- */

function Industries() {
  const items = [
    { icon: "utensils", label: "Restaurants & hospitality" },
    { icon: "stethoscope", label: "Medical & dental" },
    { icon: "hammer", label: "Construction" },
    { icon: "shoppingbag", label: "Retail & e-commerce" },
    { icon: "truck", label: "Trucking & logistics" },
    { icon: "wrench", label: "Auto & repair" },
    { icon: "briefcase", label: "Professional services" },
    { icon: "building2", label: "Manufacturing" },
  ];
  return (
    <Band tone="soft">
      <GhostNumber>06</GhostNumber>
      <Reveal>
        <SectionHead
          num="06"
          eyebrow="Industries we fund"
          title={<>We speak the language of your business.</>}
          intro="A sample of the industries we work with most. If you don't see yours, call us. Odds are we've funded one like it."
        />
      </Reveal>
      <ul className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {items.map(({ icon: __iconName, label }, i) => (
          <Reveal
            as="li"
            key={label}
            delay={i * 50}
            className="group relative overflow-hidden rounded-2xl border border-border bg-background shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
          >
            <div className="relative aspect-[4/3] overflow-hidden">
              <img
                src={IMAGES.industries[label]}
                alt={`${label} business scene`}
                className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
                loading="lazy"
              width={480} height={360}
              decoding="async"
              />
              <div
                aria-hidden
                className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/20 to-transparent"
              />
              <span className="absolute left-3 top-3 flex h-9 w-9 items-center justify-center rounded-md bg-background/90 text-accent shadow-md">
                <Icon name={__iconName} color="co" className="h-4 w-4" />
              </span>
              <p className="absolute bottom-3 left-3 right-3 font-serif text-base font-semibold text-primary-foreground">
                {label}
              </p>
            </div>
          </Reveal>
        ))}
      </ul>
    </Band>
  );
}

/* ---------------- Qualify ---------------- */

function Qualify() {
  const items = [
    { icon: "clock", k: "Time in business", v: "6+ months" },
    { icon: "trendingup", k: "Monthly revenue", v: "$15,000+" },
    { icon: "wallet", k: "Business bank account", v: "Required" },
    { icon: "mappin", k: "U.S.-based business", v: "Required" },
  ];
  return (
    <Band tone="light">
      <GhostNumber>07</GhostNumber>
      <div className="grid items-center gap-14 lg:grid-cols-[1fr_1.05fr]">
        <div>
          <SectionHead
            num="07"
            eyebrow="Who qualifies"
            title={<>Most operating businesses will qualify.</>}
            intro="These are the general basics. We've helped businesses get approved even when they didn't tick every box. Call us if you're unsure."
            align="left"
          />
          <ul className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {items.map((it, i) => (
              <Reveal
                as="li"
                key={it.k}
                delay={i * 60}
                className="rounded-xl border border-border bg-card p-5"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10 text-accent ring-1 ring-accent/20">
                  <Icon name={it.icon} color="co" className="h-4 w-4"  />
                </span>
                <p className={`${eyebrow} mt-4 text-muted-foreground`}>
                  {it.k}
                </p>
                <p className="mt-1 font-serif text-2xl font-semibold">
                  {it.v}
                </p>
              </Reveal>
            ))}
          </ul>
        </div>
        <Reveal delay={120} className="relative">
          <div
            aria-hidden
            className="absolute -right-6 -top-6 h-40 w-40 rounded-2xl bg-accent/10"
          />
          <div className="relative overflow-hidden rounded-2xl border border-border shadow-xl">
            <img
              src={IMAGES.handshake}
              alt="Two business partners shaking hands after closing a financing deal"
              className="aspect-[4/3] w-full object-cover"
              loading="lazy"
              width={700} height={525}
              decoding="async"
            />
          </div>
          <div className="absolute -bottom-5 left-6 hidden rounded-xl border border-border bg-background p-4 shadow-xl md:flex md:items-center md:gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10 text-accent">
              <Icon name="checkcircle2" color="co" className="h-5 w-5"  />
            </span>
            <div className="leading-tight">
              <p className="font-serif text-xl font-semibold">98%</p>
              <p className="text-[11px] text-muted-foreground">
                Application completion rate
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </Band>
  );
}

/* ---------------- FAQ ---------------- */

function FAQ() {
  const faqs = [
    {
      q: "Will applying hurt my credit?",
      a: "No. The initial review is a soft pull and does not affect your credit score. A hard pull only happens if you choose to accept a specific offer that requires one.",
    },
    {
      q: "How fast can I actually get funded?",
      a: "For short-term products, same-day funding is possible if your application is in by mid-morning and your documents are ready. Most clients are funded within 24 to 72 hours.",
    },
    {
      q: "What documents do I need?",
      a: "For most products: a completed application, your last 3-6 months of business bank statements, and a copy of your driver's license. SBA and larger loans require additional documentation.",
    },
    {
      q: "Do you charge any upfront fees?",
      a: "No. We are paid by the lender when a loan funds. You will never be asked for a deposit, application fee, or any payment before your loan is in your account.",
    },
    {
      q: "What if my credit isn't great?",
      a: "We work with many businesses that have less-than-perfect credit. Strong revenue and consistent deposits matter more for most of our products than your personal credit score.",
    },
    {
      q: "Am I obligated to accept an offer?",
      a: "Never. You're free to review your offers, ask questions, compare them to other quotes, or walk away entirely. There's no cost and no obligation.",
    },
  ];
  return (
    <Band tone="soft">
      <GhostNumber>08</GhostNumber>
      <Reveal>
        <SectionHead
          num="08"
          eyebrow="Questions, answered"
          title={<>Things owners ask us most.</>}
          intro="Don't see your question? Call your dedicated advisor. We'll give you a straight answer."
        />
      </Reveal>
      <div className="mx-auto mt-12 max-w-3xl">
        <FaqCards faqs={faqs} />
      </div>
    </Band>
  );
}

/* ---------------- Final CTA ---------------- */

function FinalCTA() {
  return (
    <section
      id="apply"
      className="relative overflow-hidden bg-primary text-primary-foreground"
    >
      <div aria-hidden className="absolute inset-0">
        <img
          src={IMAGES.closing}
          alt=""
          aria-hidden
          className="h-full w-full object-cover opacity-25"
          loading="lazy"
          width={1100} height={600}
              decoding="async"
        />

        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-r from-primary via-primary/85 to-primary/70"
        />
      </div>
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-24 right-0 h-80 w-80 rounded-full bg-accent/30 blur-3xl"
      />
      <div className="relative mx-auto max-w-5xl px-6 py-24 text-center md:py-32">
        <Reveal>
          <p className={`${eyebrow} text-accent`}>Ready when you are</p>
          <h2 className="mt-5 font-serif text-4xl font-semibold leading-tight tracking-tight md:text-6xl">
            Let's get your business <span className="text-accent">funded.</span>
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-lg text-primary-foreground/80">
            Apply in five minutes or call to speak with an advisor today. No
            fees, no obligation, no pressure.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Button
              asChild
              size="lg"
              className="h-12 bg-accent px-7 text-base text-accent-foreground transition hover:-translate-y-0.5 hover:bg-accent/90 hover:shadow-lg"
            >
              <Link to="/apply-funding">Apply Now</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="h-12 border-primary-foreground/30 bg-transparent px-7 text-base text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
            >
              <a href={AGENCY.phoneHref}>
                <Icon name="phonecall" color="bl" className="h-4 w-4 brightness-0 invert"  />
                Call {AGENCY.phone}
              </a>
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

